import {
  MutableRefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Document, pdfjs } from 'react-pdf'
import workerSrc from 'pdfjs-dist/build/pdf.worker.min.mjs?worker&url'
pdfjs.GlobalWorkerOptions.workerSrc = workerSrc

import { Button } from '@genericComponents/Button/Button'
import { Icon } from '@genericComponents/Icon/Icon'
import { PageRenderer } from './PageRenderer'
import './PdfViewer.css'
import { cx } from '@/utils/cx'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'
import { observer } from 'mobx-react'
import { StoreContext } from '@store/Root.store'
import { devLog } from '@/methods/devLog'
import { Indicator } from './Indicator'
import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner'
import { ScrollDocumentButton } from '@/components/ScrollDocumentButton/ScrollDocumentButton'
import { PdfErrorContainer } from '../PdfViewer/core/PdfErrorContainer'
import { getDocumentUrl } from '@/methods/getDocumentUrl'

export interface InitFunctionProps {
  nextPage: MutableRefObject<undefined | (() => void)>
  prevPage: MutableRefObject<undefined | (() => void)>
  zoomIn: MutableRefObject<undefined | (() => void)>
  zoomOut: MutableRefObject<undefined | (() => void)>
}

interface PdfViewerProps {
  file: string
  fileId?: string
  fileUrl?: string
  documentClassName?: string
  pageClassName?: string
  viewerWidth?: string
  viewerHeight?: string | number
  containerHeight?: string | number
  initialScale?: number
  onDocumentLastPage?: () => void
  onDocumentLoaded?: (numPages: number) => void
  hideZoomControls?: boolean
  showScrollToBottomButton?: boolean
  pageMargin?: number
  documentError?: string | null
  setDocumentError?: (error: string | null) => void
  controls?: {
    shouldZoomIn?: boolean
    shouldZoomOut?: boolean
    setShouldZoomIn?: (value: boolean) => void
    setShouldZoomOut?: (value: boolean) => void
  }
}

export const PdfViewer = observer(
  ({
    file,
    fileId,
    fileUrl,
    documentClassName,
    pageClassName,
    viewerWidth,
    viewerHeight,
    containerHeight,
    initialScale,
    onDocumentLastPage,
    onDocumentLoaded,
    hideZoomControls,
    showScrollToBottomButton,
    controls,
    pageMargin = 20,
    documentError,
    setDocumentError,
  }: PdfViewerProps) => {
    const store = useContext(StoreContext)
    const { zoomButtons, pdfViewer: trans } = store.TranslationsState.translationsNew

    const [isLoading, setIsLoading] = useState(true)
    const [isFocused, setIsFocused] = useState<boolean>(false)
    const [numPages, setNumPages] = useState<number>()
    const [pageScale, setPageScale] = useState<number>(1.0)

    const cachedFile = useMemo(() => file, [file])
    const cachedClassName = useMemo(() => documentClassName, [documentClassName])
    const cachedOptions = useMemo(() => ({ enableHWA: true }), [])
    const onDocumentLoadSuccess = useCallback(({ numPages }: pdfjs.PDFDocumentProxy) => {
      setNumPages(numPages)
      onDocumentLoaded && onDocumentLoaded(numPages)
      setIsLoading(false)
    }, [])

    useEffect(() => {
      if (initialScale) {
        setPageScale(initialScale)
      }
    }, [])

    useEffect(() => {
      if (controls?.shouldZoomIn) {
        setPageScale(pageScale + 0.2)

        if (controls?.setShouldZoomIn) {
          controls.setShouldZoomIn(false)
        }
      }
    }, [controls?.shouldZoomIn])

    useEffect(() => {
      if (controls?.shouldZoomOut) {
        setPageScale(pageScale - 0.2)

        if (controls?.setShouldZoomOut) {
          controls.setShouldZoomOut(false)
        }
      }
    }, [controls?.shouldZoomOut])

    const InnerWrapRef = useRef<HTMLDivElement>(null)
    const [innerHeight, setInnerHeight] = useState<string | number>(
      containerHeight || viewerHeight || 0
    )

    useEffect(() => {
      if (containerHeight) {
        setInnerHeight(containerHeight)
      }

      if (viewerHeight && typeof viewerHeight === 'number') {
        setInnerHeight(viewerHeight)
      }

      if (viewerHeight && typeof viewerHeight === 'string') {
        setInnerHeight(`${InnerWrapRef?.current?.clientHeight || 0}px`)
      }
    }, [containerHeight, viewerHeight])

    const [pageRendererKey, setPageRendererKey] = useState(0)

    const [innerWrapRefDimensions, setInnerWrapRefDimensions] = useState({
      width: 0,
      height: 0,
    })

    useEffect(() => {
      if (InnerWrapRef?.current) {
        const { width, height } = InnerWrapRef.current.getBoundingClientRect()
        setInnerWrapRefDimensions({ width, height })
        setPageRendererKey((prev) => prev + 1)
      }
    }, [InnerWrapRef.current])

    const [currentPageIndex, setCurrentPageIndex] = useState(1)

    useEffect(() => {
      if (numPages === 1) {
        devLog('single page doc')
        onDocumentLastPage && onDocumentLastPage()
      }
    }, [currentPageIndex, numPages, fileId])

    const [shouldShowIndicator, setShouldShowIndicator] = useState(false)

    const handleDocumentLoadError = useCallback(
      (error: Error) => {
        setDocumentError && setDocumentError(error.message)
        setIsLoading(false)
      },
      [setDocumentError]
    )

    const handlePageIndicatorVisibility = useCallback(() => {
      setShouldShowIndicator(true)
    }, [])

    if (documentError) {
      return (
        <PdfErrorContainer
          width={viewerWidth}
          height={containerHeight || viewerHeight}
          failedToLoadPdf={trans?.cantDisplayTheDocument || ''}
          documentUrl={fileUrl || getDocumentUrl(fileId)}
        />
      )
    }

    return (
      <div
        className={cx('pdf-old__container', isFocused && 'pdf-old__container--focused')}
        style={{
          width: viewerWidth || '500px',
          height: containerHeight || viewerHeight || '700px',
          marginLeft: viewerWidth === '100vw' ? '-40px' : '0',
        }}
        ref={InnerWrapRef}
        data-testid="pdf-viewer"
        data-loaded={!isLoading}
      >
        <ScrollDocumentButton
          onClick={onDocumentLastPage}
          showScrollToBottomButton={showScrollToBottomButton}
        />
        <Document
          file={cachedFile}
          onLoadSuccess={onDocumentLoadSuccess}
          className={cachedClassName}
          options={cachedOptions}
          onLoadError={handleDocumentLoadError}
          onLoadStart={() => setNumPages(undefined)}
          loading={
            <LoadingSpinner
              style={{
                padding: `calc(${containerHeight ? containerHeight : viewerHeight}/2 - 70px) 0`,
              }}
            />
          }
          error={
            <PdfErrorContainer
              width={viewerWidth}
              height={containerHeight || viewerHeight}
              failedToLoadPdf={trans?.cantDisplayTheDocument || ''}
              documentUrl={fileUrl || getDocumentUrl(fileId)}
            />
          }
        >
          <PageRenderer
            key={'page-renderer' + pageRendererKey}
            numPages={numPages || 0}
            pageClassName={pageClassName}
            canvasWidth={innerWrapRefDimensions.width - pageMargin}
            pageScale={pageScale}
            viewerWidth={viewerWidth}
            viewerHeight={innerHeight}
            containerDimensions={innerWrapRefDimensions}
            onDocumentLastPage={onDocumentLastPage}
            setCurrentPageIndex={setCurrentPageIndex}
            handlePageIndicatorVisibility={handlePageIndicatorVisibility}
            onFocusHandler={setIsFocused}
          />
        </Document>

        <Indicator
          currentPage={currentPageIndex}
          totalPages={numPages}
          shouldShow={shouldShowIndicator}
          setShouldShow={setShouldShowIndicator}
        />

        {!hideZoomControls && (
          <div className="pdf-old__navigation onboarding-pdf-viewer-navigation">
            <div className="pdf-old__navigation-inner onboarding-pdf-viewer-navigation-inner">
              <Button
                className="button--pdf-zoom"
                onClick={() => setPageScale(pageScale - 0.2)}
                tabIndex={0}
                aria-label={zoomButtons?.zoomOut}
              >
                <Icon type="zoom-out" size="30px" />
              </Button>

              <Button
                className="button--pdf-zoom"
                onClick={() => setPageScale(pageScale + 0.2)}
                tabIndex={0}
                aria-label={zoomButtons?.zoomIn}
              >
                <Icon type="zoom-in" size="30px" />
              </Button>
            </div>
          </div>
        )}
      </div>
    )
  }
)
